import { useEffect, useState } from "react";
import { fetchApplications, type PortalApplication } from "./api.js";
import { ArrowRight, CardSkeleton, EmptyState, Notice, formatDate } from "./ui.js";

/**
 * Every application, across every programme and every cycle — the point of the platform, and the
 * first screen after signing in.
 *
 * `reviewStatus` is deliberately absent: an applicant reading "REJECTED" in a portal before a
 * person has told them is exactly what §11 guards against by defaulting status emails off. The
 * badges here say what happened to the *submission*, never what a reviewer decided.
 */
export const Applications = ({
  onOpen,
  onEdit,
}: {
  onOpen: (id: string) => void;
  onEdit: (id: string) => void;
}): JSX.Element => {
  const [applications, setApplications] = useState<PortalApplication[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let live = true;
    void fetchApplications()
      .then((result) => {
        if (live) setApplications(result.data);
      })
      .catch((caught: unknown) => {
        if (live) {
          setError(
            caught instanceof Error ? caught.message : "Your applications could not be loaded.",
          );
        }
      });
    return () => {
      live = false;
    };
  }, []);

  if (error) return <Notice tone="error">{error}</Notice>;
  if (!applications) return <CardSkeleton />;

  return (
    <div>
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <h1 className="font-heading text-2xl font-semibold">My applications</h1>
        {applications.length > 0 && (
          <p className="sga-muted text-sm">
            {applications.length} application{applications.length === 1 ? "" : "s"}
          </p>
        )}
      </div>

      {applications.length === 0 ? (
        <div className="mt-5">
          <EmptyState title="Nothing here yet">
            When you apply to a Seplat Growth Academy programme, it appears here — every programme,
            every year, in one place. Open the link you were given for a programme to start.
          </EmptyState>
        </div>
      ) : (
        <ul className="mt-5 space-y-3">
          {applications.map((application) => (
            <li key={application.id}>
              {/* The whole card is the control, not a "View" button in its corner: a 44px target
                  on a phone is easy to miss, and a full card is impossible to. */}
              <button
                type="button"
                onClick={() => onOpen(application.id)}
                className="sga-card sga-card--interactive flex w-full items-center gap-4 p-4 text-left"
              >
                <span className="min-w-0 flex-1">
                  <span className="font-heading block font-semibold">{application.formTitle}</span>

                  <span className="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-1.5">
                    {application.cycle && <span className="sga-badge">{application.cycle}</span>}
                    {application.status === "SUPERSEDED" && (
                      <span className="sga-badge">Replaced by a later submission</span>
                    )}
                    {application.canEdit && (
                      // Was a badge saying "Can still be amended" beside a sentence pointing at a
                      // link in the confirmation email that never existed. It does something now.
                      <button
                        type="button"
                        onClick={(event) => {
                          event.stopPropagation();
                          onEdit(application.id);
                        }}
                        className="sga-badge sga-badge--current"
                      >
                        Change my answers
                      </button>
                    )}
                    <span className="sga-muted text-xs">
                      {application.submittedAt
                        ? `Submitted ${formatDate(application.submittedAt)}`
                        : "Not submitted"}
                    </span>
                  </span>

                  {application.reference && (
                    <span
                      className="sga-reference mt-1.5 block"
                      style={{ color: "var(--sga-primary)" }}
                    >
                      {application.reference}
                    </span>
                  )}
                </span>

                <ArrowRight className="h-5 w-5 shrink-0 text-[color:var(--sga-muted)]" />
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};
